import React, { useState, useRef } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import client from '../../lib/feathers';

const DESIGNATION_TYPES = [
  { value: 'ava', label: 'AVA' },
  { value: 'aoc', label: 'AOC' },
  { value: 'aop', label: 'AOP' },
  { value: 'doc', label: 'DOC' },
  { value: 'docg', label: 'DOCG' },
  { value: 'do', label: 'DO' },
  { value: 'doca', label: 'DOCa' },
  { value: 'gi', label: 'GI' },
  { value: 'wo', label: 'WO' },
  { value: 'vqa', label: 'VQA' },
  { value: 'ig', label: 'IG' },
];

/**
 * Extract polygon/multipolygon features from any GeoJSON structure.
 */
function extractFeatures(geojson) {
  if (geojson.type === 'FeatureCollection') {
    return (geojson.features || []).filter(
      f => f.geometry && (f.geometry.type === 'Polygon' || f.geometry.type === 'MultiPolygon')
    );
  }
  if (geojson.type === 'Feature') {
    if (geojson.geometry && (geojson.geometry.type === 'Polygon' || geojson.geometry.type === 'MultiPolygon')) {
      return [geojson];
    }
    return [];
  }
  if (geojson.type === 'Polygon' || geojson.type === 'MultiPolygon') {
    return [{ type: 'Feature', properties: {}, geometry: geojson }];
  }
  return [];
}

export default function GeoJSONImportModal({ onClose, onImportComplete }) {
  const fileInputRef = useRef(null);
  const [features, setFeatures] = useState([]);
  const [propertyKeys, setPropertyKeys] = useState([]);
  const [nameField, setNameField] = useState('');
  const [defaultDesignationType, setDefaultDesignationType] = useState('ava');
  const [defaultCountry, setDefaultCountry] = useState('US');
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [results, setResults] = useState(null);
  const [fileName, setFileName] = useState('');

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const geojson = JSON.parse(evt.target.result);
        const extracted = extractFeatures(geojson);
        setFeatures(extracted);

        // Collect all property keys across features
        const keys = new Set();
        extracted.forEach(f => {
          if (f.properties) {
            Object.keys(f.properties).forEach(k => keys.add(k));
          }
        });
        const keyList = Array.from(keys);
        setPropertyKeys(keyList);

        // Auto-detect name field
        const nameCandidate = keyList.find(k =>
          ['name', 'NAME', 'ava_name', 'AVA_NAME', 'title', 'TITLE'].includes(k)
        );
        if (nameCandidate) setNameField(nameCandidate);
      } catch (err) {
        console.error('Error parsing GeoJSON:', err);
        alert('Failed to parse GeoJSON file. Ensure it is valid JSON.');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleImport = async () => {
    if (!nameField) {
      alert('Please select which property to use as the appellation name.');
      return;
    }

    const toImport = features.filter(f => {
      const name = f.properties?.[nameField];
      return name && name.toString().trim();
    });

    if (toImport.length === 0) {
      alert('No features with valid names found.');
      return;
    }

    setImporting(true);
    setProgress({ current: 0, total: toImport.length });

    let created = 0;
    let skipped = 0;
    let errors = 0;

    const service = client.service('admin/appellation');

    for (let i = 0; i < toImport.length; i++) {
      const feature = toImport[i];
      const name = feature.properties[nameField].toString().trim();
      setProgress({ current: i + 1, total: toImport.length });

      try {
        await service.create({
          name,
          designationType: defaultDesignationType,
          country: defaultCountry,
          boundary: feature.geometry,
          active: true,
        });
        created++;
      } catch (err) {
        if (err.message && err.message.includes('unique')) {
          skipped++;
        } else {
          console.error(`Error importing ${name}:`, err.message);
          errors++;
        }
      }
    }

    setResults({ created, skipped, errors, total: toImport.length });
    setImporting(false);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
        <div className="relative bg-white rounded-lg shadow-xl max-w-3xl w-full p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Import GeoJSON</h3>
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-500">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {results ? (
            /* Results */
            <div>
              <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-4">
                <h4 className="font-medium text-green-800 mb-2">Import Complete</h4>
                <ul className="text-sm text-green-700 space-y-1">
                  <li>Created: {results.created}</li>
                  <li>Skipped (duplicates): {results.skipped}</li>
                  <li>Errors: {results.errors}</li>
                </ul>
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={onImportComplete}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-red-700"
                >
                  Done
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Step 1: File Upload */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  1. Select GeoJSON File
                </label>
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-gray-400"
                >
                  {fileName ? (
                    <div>
                      <p className="text-sm font-medium text-gray-900">{fileName}</p>
                      <p className="text-sm text-gray-500">{features.length} polygon features found</p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm text-gray-500">Click to select a .geojson or .json file</p>
                      <p className="text-xs text-gray-400 mt-1">Supports FeatureCollections, single Features, or bare geometries</p>
                    </div>
                  )}
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept=".geojson,.json"
                  className="hidden"
                />
              </div>

              {features.length > 0 && (
                <>
                  {/* Step 2: Field Mapping */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      2. Map Name Field
                    </label>
                    <select
                      value={nameField}
                      onChange={(e) => setNameField(e.target.value)}
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                    >
                      <option value="">-- Select property for appellation name --</option>
                      {propertyKeys.map(k => (
                        <option key={k} value={k}>{k}</option>
                      ))}
                    </select>
                  </div>

                  {/* Step 3: Defaults */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      3. Set Defaults
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Designation Type</label>
                        <select
                          value={defaultDesignationType}
                          onChange={(e) => setDefaultDesignationType(e.target.value)}
                          className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                        >
                          {DESIGNATION_TYPES.map(t => (
                            <option key={t.value} value={t.value}>{t.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Country</label>
                        <input
                          type="text"
                          maxLength={2}
                          value={defaultCountry}
                          onChange={(e) => setDefaultCountry(e.target.value.toUpperCase())}
                          className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Preview */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Preview ({features.length} features)
                    </label>
                    <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Name</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Geometry</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {features.slice(0, 50).map((f, i) => (
                            <tr key={i}>
                              <td className="px-3 py-1.5 text-sm text-gray-900">
                                {nameField ? (f.properties?.[nameField] || <span className="text-red-400">Missing</span>) : '--'}
                              </td>
                              <td className="px-3 py-1.5 text-sm text-gray-500">
                                {f.geometry?.type}
                              </td>
                            </tr>
                          ))}
                          {features.length > 50 && (
                            <tr>
                              <td colSpan="2" className="px-3 py-1.5 text-xs text-gray-400">
                                ...and {features.length - 50} more
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Import Progress */}
                  {importing && (
                    <div className="mb-4">
                      <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
                        <span>Importing...</span>
                        <span>{progress.current} / {progress.total}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-primary h-2 rounded-full transition-all"
                          style={{ width: `${(progress.current / progress.total) * 100}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                      disabled={importing}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleImport}
                      disabled={importing || !nameField}
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-red-700 disabled:opacity-50"
                    >
                      {importing ? 'Importing...' : `Import ${features.length} Appellations`}
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
